import React from 'react';

export const AgentExplainer: React.FC = () => {
  return (
    <div style={{
      maxWidth: '900px',
      margin: '0 auto',
      padding: '2rem',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ fontSize: '2rem', fontWeight: '700', color: '#1f2937', marginBottom: '0.5rem' }}>
          🤖 How AI Chat Agents Work
        </h1>
        <p style={{ fontSize: '1.1rem', color: '#6b7280', lineHeight: '1.6' }}>
          Understanding the components that power intelligent WhatsApp conversations
        </p>
      </div>

      {/* Overview */}
      <section style={{ marginBottom: '3rem' }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: '600', color: '#1f2937', marginBottom: '1rem' }}>
          📖 Overview
        </h2>
        <p style={{ fontSize: '1rem', color: '#374151', lineHeight: '1.8', marginBottom: '1rem' }}>
          AI Chat Agents are intelligent assistants that handle WhatsApp conversations automatically. They qualify leads,
          answer questions, schedule appointments, and escalate complex cases to human specialists when needed.
        </p>
        <div style={{
          background: '#f3f4f6',
          padding: '1.5rem',
          borderRadius: '8px',
          borderLeft: '4px solid #f97316'
        }}>
          <p style={{ fontSize: '0.95rem', color: '#374151', margin: 0, lineHeight: '1.6' }}>
            <strong>Key Benefit:</strong> Respond to leads 24/7 with consistent, accurate information while freeing your team
            to focus on high-value consultations.
          </p>
        </div>
      </section>

      {/* Core Components */}
      <section style={{ marginBottom: '3rem' }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: '600', color: '#1f2937', marginBottom: '1.5rem' }}>
          🧩 Core Components
        </h2>

        {/* System Prompt */}
        <div style={{ marginBottom: '2rem' }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#1f2937', marginBottom: '1rem' }}>
            📝 System Prompt
          </h3>
          <p style={{ fontSize: '1rem', color: '#374151', lineHeight: '1.8', marginBottom: '1rem' }}>
            The system prompt is the <strong>instruction manual</strong> for your AI agent. It defines:
          </p>
          <ul style={{ fontSize: '1rem', color: '#374151', lineHeight: '2', paddingLeft: '1.5rem' }}>
            <li><strong>Identity:</strong> Who the agent is (e.g., "Immigration Lead Management Agent")</li>
            <li><strong>Objectives:</strong> Primary goals (qualify leads, answer questions, schedule appointments)</li>
            <li><strong>Domain Scope:</strong> What topics it can discuss (e.g., Singapore immigration services)</li>
            <li><strong>Behavioral Rules:</strong> How to communicate, when to escalate, what to avoid</li>
            <li><strong>Source of Truth:</strong> Where to get information (knowledge base, approved URLs)</li>
          </ul>
          <div style={{
            background: '#fef3c7',
            padding: '1rem',
            borderRadius: '6px',
            marginTop: '1rem'
          }}>
            <p style={{ fontSize: '0.9rem', color: '#78350f', margin: 0 }}>
              💡 <strong>Best Practice:</strong> Be specific and comprehensive. A detailed system prompt prevents hallucinations
              and ensures consistent behavior.
            </p>
          </div>
        </div>

        {/* Temperature */}
        <div style={{ marginBottom: '2rem' }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#1f2937', marginBottom: '1rem' }}>
            🌡️ Temperature (0.0 - 1.0)
          </h3>
          <p style={{ fontSize: '1rem', color: '#374151', lineHeight: '1.8', marginBottom: '1rem' }}>
            Controls the <strong>creativity vs consistency</strong> of responses:
          </p>

          <div style={{ display: 'grid', gap: '1rem', marginBottom: '1rem' }}>
            <div style={{
              background: '#dbeafe',
              padding: '1rem',
              borderRadius: '6px',
              borderLeft: '4px solid #3b82f6'
            }}>
              <h4 style={{ fontSize: '1rem', fontWeight: '600', color: '#1e40af', margin: '0 0 0.5rem 0' }}>
                Low Temperature (0.0 - 0.3) ❄️
              </h4>
              <p style={{ fontSize: '0.9rem', color: '#1e3a8a', margin: 0, lineHeight: '1.6' }}>
                <strong>Recommended for:</strong> Lead qualification, factual Q&A, appointment scheduling
                <br />
                <strong>Behavior:</strong> Predictable, consistent, follows system prompt strictly
                <br />
                <strong>Example:</strong> 0.05 (WhatsApp Sales Assistant uses this)
              </p>
            </div>

            <div style={{
              background: '#fef3c7',
              padding: '1rem',
              borderRadius: '6px',
              borderLeft: '4px solid #f59e0b'
            }}>
              <h4 style={{ fontSize: '1rem', fontWeight: '600', color: '#92400e', margin: '0 0 0.5rem 0' }}>
                Medium Temperature (0.4 - 0.7) 🌤️
              </h4>
              <p style={{ fontSize: '0.9rem', color: '#78350f', margin: 0, lineHeight: '1.6' }}>
                <strong>Recommended for:</strong> Customer support with personality, conversational engagement
                <br />
                <strong>Behavior:</strong> Balanced creativity and reliability
                <br />
                <strong>Example:</strong> 0.5
              </p>
            </div>

            <div style={{
              background: '#fed7aa',
              padding: '1rem',
              borderRadius: '6px',
              borderLeft: '4px solid #ea580c'
            }}>
              <h4 style={{ fontSize: '1rem', fontWeight: '600', color: '#7c2d12', margin: '0 0 0.5rem 0' }}>
                High Temperature (0.8 - 1.0) 🔥
              </h4>
              <p style={{ fontSize: '0.9rem', color: '#7c2d12', margin: 0, lineHeight: '1.6' }}>
                <strong>Recommended for:</strong> Creative content generation, brainstorming
                <br />
                <strong>Behavior:</strong> Unpredictable, creative, may deviate from instructions
                <br />
                <strong>⚠️ Not recommended for business-critical conversations</strong>
              </p>
            </div>
          </div>
        </div>

        {/* Fallback Message */}
        <div style={{ marginBottom: '2rem' }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#1f2937', marginBottom: '1rem' }}>
            💬 Fallback Message
          </h3>
          <p style={{ fontSize: '1rem', color: '#374151', lineHeight: '1.8', marginBottom: '1rem' }}>
            The message sent when the AI cannot answer a question. This ensures customers always get a response,
            even if it's a handoff to a human specialist.
          </p>
          <div style={{
            background: '#f3f4f6',
            padding: '1rem',
            borderRadius: '6px',
            fontStyle: 'italic',
            border: '1px solid #d1d5db'
          }}>
            <p style={{ fontSize: '0.9rem', color: '#374151', margin: 0 }}>
              "Sorry, we will need to check with our specialist on your question and revert later"
            </p>
          </div>
        </div>

        {/* Knowledge Base */}
        <div style={{ marginBottom: '2rem' }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#1f2937', marginBottom: '1rem' }}>
            📚 Knowledge Base (RAG)
          </h3>
          <p style={{ fontSize: '1rem', color: '#374151', lineHeight: '1.8', marginBottom: '1rem' }}>
            Retrieval-Augmented Generation (RAG) allows your AI to access verified documents and data sources:
          </p>
          <ul style={{ fontSize: '1rem', color: '#374151', lineHeight: '2', paddingLeft: '1.5rem' }}>
            <li><strong>Internal Documents:</strong> Policy documents, FAQs, service scope, pricing guides</li>
            <li><strong>Government Guidelines:</strong> Official ICA, MOM, ACRA regulations</li>
            <li><strong>Historical Cases:</strong> Anonymized past cases for pattern recognition</li>
            <li><strong>Playbooks:</strong> Step-by-step conversation flows for specific scenarios</li>
          </ul>
          <div style={{
            background: '#dbeafe',
            padding: '1rem',
            borderRadius: '6px',
            marginTop: '1rem'
          }}>
            <p style={{ fontSize: '0.9rem', color: '#1e3a8a', margin: 0 }}>
              🔒 <strong>Source of Truth:</strong> The AI will ONLY use information from the knowledge base.
              If information isn't found, it will escalate to a human rather than guess.
            </p>
          </div>
        </div>

        {/* Tools */}
        <div style={{ marginBottom: '2rem' }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#1f2937', marginBottom: '1rem' }}>
            🛠️ Tools & Integrations
          </h3>
          <p style={{ fontSize: '1rem', color: '#374151', lineHeight: '1.8', marginBottom: '1rem' }}>
            AI agents can use various tools to take actions:
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
            <div style={{ background: '#f9fafb', padding: '1rem', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
              <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>📅</div>
              <h4 style={{ fontSize: '1rem', fontWeight: '600', color: '#1f2937', margin: '0 0 0.5rem 0' }}>
                Calendar Scheduling
              </h4>
              <p style={{ fontSize: '0.9rem', color: '#6b7280', margin: 0 }}>
                Book appointments with sales team or consultants
              </p>
            </div>
            <div style={{ background: '#f9fafb', padding: '1rem', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
              <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>💾</div>
              <h4 style={{ fontSize: '1rem', fontWeight: '600', color: '#1f2937', margin: '0 0 0.5rem 0' }}>
                CRM Integration
              </h4>
              <p style={{ fontSize: '0.9rem', color: '#6b7280', margin: 0 }}>
                Create and update lead records automatically
              </p>
            </div>
            <div style={{ background: '#f9fafb', padding: '1rem', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
              <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>🔍</div>
              <h4 style={{ fontSize: '1rem', fontWeight: '600', color: '#1f2937', margin: '0 0 0.5rem 0' }}>
                Knowledge Retrieval
              </h4>
              <p style={{ fontSize: '0.9rem', color: '#6b7280', margin: 0 }}>
                Search internal documents and FAQs
              </p>
            </div>
            <div style={{ background: '#f9fafb', padding: '1rem', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
              <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>👤</div>
              <h4 style={{ fontSize: '1rem', fontWeight: '600', color: '#1f2937', margin: '0 0 0.5rem 0' }}>
                Human Handoff
              </h4>
              <p style={{ fontSize: '0.9rem', color: '#6b7280', margin: 0 }}>
                Escalate complex cases to specialists
              </p>
            </div>
          </div>

          {/* Important Note about Tool Authentication */}
          <div style={{
            marginTop: '1.5rem',
            background: '#fff7ed',
            padding: '1.25rem',
            borderRadius: '8px',
            border: '2px solid #fb923c',
            borderLeft: '6px solid #ea580c'
          }}>
            <h4 style={{ fontSize: '1rem', fontWeight: '600', color: '#9a3412', margin: '0 0 0.75rem 0', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ fontSize: '1.25rem' }}>⚠️</span>
              Important: Tool Authentication & Per-Agent Permissions
            </h4>
            <div style={{ fontSize: '0.9rem', color: '#7c2d12', lineHeight: '1.8' }}>
              <p style={{ margin: '0 0 0.75rem 0' }}>
                <strong>Tools must be authenticated before they can be used:</strong>
              </p>
              <ul style={{ margin: '0 0 0.75rem 0', paddingLeft: '1.5rem' }}>
                <li><strong>Calendar Scheduling</strong> - Requires Google Calendar OAuth authentication (Admin → Integrations → Google Calendar)</li>
                <li><strong>CRM Integration</strong> - Requires CRM system connection (Admin → Integrations → CRM)</li>
                <li><strong>Knowledge Retrieval (RAG)</strong> - Always available (uses your uploaded documents)</li>
                <li><strong>Human Handoff</strong> - Always available (built-in system feature)</li>
              </ul>
              <p style={{ margin: '0 0 0.75rem 0' }}>
                <strong>Each agent has individual tool permissions:</strong> When creating an agent version, you select which tools that specific agent can use. Even if a tool is authenticated, an agent can only use it if you've enabled it for that agent.
              </p>
              <p style={{ margin: 0 }}>
                <strong>Tool Configuration Page:</strong> Visit <em>Admin → 🔧 Tool Configuration</em> to view tool authentication status, usage statistics, and execution history.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Example: WhatsApp Sales Assistant */}
      <section style={{ marginBottom: '3rem' }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: '600', color: '#1f2937', marginBottom: '1.5rem' }}>
          💼 Example: WhatsApp Sales Assistant
        </h2>
        <div style={{
          background: '#fafafa',
          padding: '1.5rem',
          borderRadius: '8px',
          border: '1px solid #e5e7eb'
        }}>
          <div style={{ marginBottom: '1rem' }}>
            <h4 style={{ fontSize: '1rem', fontWeight: '600', color: '#374151', marginBottom: '0.5rem' }}>
              Configuration:
            </h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', fontSize: '0.9rem' }}>
              <div>
                <span style={{ color: '#6b7280' }}>Temperature:</span>{' '}
                <strong style={{ color: '#1f2937' }}>0.05</strong> (Very low - factual)
              </div>
            </div>
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <h4 style={{ fontSize: '1rem', fontWeight: '600', color: '#374151', marginBottom: '0.5rem' }}>
              Key Behaviors:
            </h4>
            <ul style={{ fontSize: '0.9rem', color: '#374151', lineHeight: '1.8', paddingLeft: '1.5rem', margin: 0 }}>
              <li>Specializes in Singapore immigration services only</li>
              <li>Uses RAG to retrieve playbooks for specific scenarios (e.g., Denial of Entry)</li>
              <li>Asks progressive questions instead of overwhelming users</li>
              <li>Escalates when confidence is low or user is emotional</li>
              <li>Never guarantees approvals or outcomes</li>
            </ul>
          </div>

          <div>
            <h4 style={{ fontSize: '1rem', fontWeight: '600', color: '#374151', marginBottom: '0.5rem' }}>
              Fallback Message:
            </h4>
            <div style={{
              background: '#fff',
              padding: '0.75rem',
              borderRadius: '4px',
              border: '1px solid #d1d5db',
              fontStyle: 'italic',
              fontSize: '0.9rem',
              color: '#374151'
            }}>
              "Sorry, we will need to check with our specialist on your question and revert later"
            </div>
          </div>
        </div>
      </section>

      {/* Best Practices */}
      <section style={{ marginBottom: '3rem' }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: '600', color: '#1f2937', marginBottom: '1.5rem' }}>
          ✨ Best Practices
        </h2>
        <div style={{ display: 'grid', gap: '1rem' }}>
          <div style={{
            background: '#dcfce7',
            padding: '1rem',
            borderRadius: '6px',
            borderLeft: '4px solid #16a34a'
          }}>
            <h4 style={{ fontSize: '1rem', fontWeight: '600', color: '#14532d', margin: '0 0 0.5rem 0' }}>
              ✅ Do: Be Specific in System Prompts
            </h4>
            <p style={{ fontSize: '0.9rem', color: '#14532d', margin: 0 }}>
              Define exact scope, prohibited behaviors, escalation rules, and success metrics. The more detailed, the better.
            </p>
          </div>

          <div style={{
            background: '#dcfce7',
            padding: '1rem',
            borderRadius: '6px',
            borderLeft: '4px solid #16a34a'
          }}>
            <h4 style={{ fontSize: '1rem', fontWeight: '600', color: '#14532d', margin: '0 0 0.5rem 0' }}>
              ✅ Do: Test with Draft Versions
            </h4>
            <p style={{ fontSize: '0.9rem', color: '#14532d', margin: 0 }}>
              Create draft versions, test with real scenarios, refine based on results, then promote to production.
            </p>
          </div>

          <div style={{
            background: '#dcfce7',
            padding: '1rem',
            borderRadius: '6px',
            borderLeft: '4px solid #16a34a'
          }}>
            <h4 style={{ fontSize: '1rem', fontWeight: '600', color: '#14532d', margin: '0 0 0.5rem 0' }}>
              ✅ Do: Keep Knowledge Base Updated
            </h4>
            <p style={{ fontSize: '0.9rem', color: '#14532d', margin: 0 }}>
              Regularly update your RAG documents with latest policies, FAQs, and approved answers.
            </p>
          </div>

          <div style={{
            background: '#fee2e2',
            padding: '1rem',
            borderRadius: '6px',
            borderLeft: '4px solid #dc2626'
          }}>
            <h4 style={{ fontSize: '1rem', fontWeight: '600', color: '#7f1d1d', margin: '0 0 0.5rem 0' }}>
              ❌ Don't: Use High Temperature for Critical Tasks
            </h4>
            <p style={{ fontSize: '0.9rem', color: '#7f1d1d', margin: 0 }}>
              Avoid temperatures above 0.3 for lead qualification, appointment booking, or factual Q&A.
            </p>
          </div>

          <div style={{
            background: '#fee2e2',
            padding: '1rem',
            borderRadius: '6px',
            borderLeft: '4px solid #dc2626'
          }}>
            <h4 style={{ fontSize: '1rem', fontWeight: '600', color: '#7f1d1d', margin: '0 0 0.5rem 0' }}>
              ❌ Don't: Skip Testing Before Production
            </h4>
            <p style={{ fontSize: '0.9rem', color: '#7f1d1d', margin: 0 }}>
              Always test in draft stage with edge cases, angry customers, and unclear inquiries before promoting.
            </p>
          </div>
        </div>
      </section>

      {/* Footer CTA */}
      <div style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '2rem',
        borderRadius: '12px',
        color: 'white',
        textAlign: 'center'
      }}>
        <h3 style={{ fontSize: '1.5rem', fontWeight: '600', margin: '0 0 1rem 0' }}>
          Ready to Build Your AI Agent?
        </h3>
        <p style={{ fontSize: '1rem', margin: '0 0 1.5rem 0', opacity: 0.9 }}>
          Use our Template Library to get started with proven configurations
        </p>
        <button
          onClick={() => window.history.back()}
          style={{
            background: 'white',
            color: '#667eea',
            padding: '0.75rem 2rem',
            borderRadius: '6px',
            border: 'none',
            fontSize: '1rem',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          Go Back to Agent Factory
        </button>
      </div>
    </div>
  );
};
